import type { CookedProduct, CookedProductList } from '@apps/registry/common/selectors/ProductListSelector';
import { isPricedProduct } from '@apps/registry/common/selectors/ProductListSelector';
import type { ShippingAddressFragment } from '@graphql/fragments/shippingAddress.graphql.generated';
import type { ECard } from '@graphql/generated-base';
import { useCreateRegistryPaymentIntentMutation } from '@graphql/mutations/order.graphql.generated';
import { useGetCalculateRegistryPurchaseInfoQuery } from '@graphql/queries/registry.graphql.generated';
import { createContext } from '@shared/utils/createContext';
import type { GuestRegistryState } from '@apps/registry/guest/GuestRegistry.controller';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { GiftWrapOfferState } from '@apps/registry/guest/components/CheckoutDialog/components/GiftWrapOffer/GiftWrapOffer';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { prepareECardPayload, useGiftWrapECardPrice, useSaveCardDraft } from '@shared/components/GiftWrap';
import type { LineItem, StripeExpressCheckoutElementClickEvent, StripeExpressCheckoutElementConfirmEvent, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';
import { GiftWrapType } from '../ShoppingCart/ShoppingCart.telemetry';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { useFullPageLoader } from '@shared/components/JoyLogoLoader/FullPageLoaderProvider';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import type { ExpressCheckoutPurchaseItem } from './ExpressCheckout';
import { shippingAddressFragmentAsInput } from '../../utils/shippingAddress';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { isSomePaymentMethodAvailable } from '@shared/core/stripe';
import { deriveAggregateCheckoutCodesResult } from '../../utils/checkoutMechanisms';
import { composePurchaseReturnUrlTemplate } from '@apps/registry/guest/hooks/usePurchaseConfirmationLink';
import { computeRegistryPurchaseInfoCostLines } from '../../utils/additionalCostLines';
import type { PurchaseInfoCostLines } from '../../utils/additionalCostLines';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { canShowGW } from '@apps/registry/guest/components/CheckoutDialog/hooks/useCheckoutAllowGWByLocation/utils';
import { isBillingAddressValid } from '@apps/registry/guest/components/CheckoutDialog/hooks/useCheckoutAllowGWByLocation/useCheckoutAllowGWByLocation';
import { findAlpha3ByAlpha2, findCountryNameByAlpha2Code } from '@shared/hooks/useGeoLocale/countries';

export interface MonetaryValue {
  valueInMinorUnits: number;
}

export enum ExpressCheckoutShippingOption {
  CoupleAddress = 'CoupleAddress',
  ShipToMe = 'ShipToMe',
}

export enum InternalCheckoutErrorCode {
  serverError = 'server_error',
  missingClientSecret = 'missing_client_secret',
  missingConfirmationToken = 'missing_confirmation_token',
  insufficientData = 'insufficient_data',
  unknownError = 'unknown_error',
  forbiddenShippingCountry = 'forbidden_shipping_country',
}

export interface CheckoutError {
  readonly code?: InternalCheckoutErrorCode | string;
  readonly message?: string;
}

export type ProductWithQuantity = CookedProduct & { quantity: number; totalValueInMinorUnits: number };

interface State {
  purchaseProducts: ProductWithQuantity[];
  productsPrice: MonetaryValue;
  hasPhysicalProducts: boolean;
  disablePaidGiftWrap: boolean;
  totalPrice: MonetaryValue;
  currencyCode: string;
  shippingOption: ExpressCheckoutShippingOption;
  /** Placeholder for giftwrap message, which will be replaced with user name after purchase */
  messageGiverNamePlaceholder: string;

  checkoutError?: CheckoutError | null;
  eCardPrice?: MonetaryValue | null;
  giftWrap?: GiftWrapOfferState | null;
  costInfo: PurchaseInfoCostLines | null;
  coupleAddress?: ShippingAddressFragment | null;
  supportsShipToMe: boolean;
}

interface Mutators {
  setGiftWrap: (giftWrap: GiftWrapOfferState) => void;
  setShippingOption: (option: ExpressCheckoutShippingOption) => void;
  handleCheckoutClick: (event: StripeExpressCheckoutElementClickEvent) => void;
  handleCheckoutConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  handleCheckoutReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
}

interface ExpressCheckoutProviderProps {
  eventHandle: string;
  eventId: string;
  purchaseItems: ExpressCheckoutPurchaseItem[];
  registryState: GuestRegistryState;
  allProducts: CookedProductList;
}

interface ExpressCheckoutContext {
  state: State;
  mutators: Mutators;
}

export const [Provider, useExpressCheckoutContext] = createContext<ExpressCheckoutContext>({ name: 'ExpressCheckout' });

export const ExpressCheckoutProvider: React.FC<ExpressCheckoutProviderProps> = ({ eventHandle, eventId, purchaseItems, registryState, allProducts, children }) => {
  const coupleAddress = registryState.shippingAddress;
  const elements = useElements();
  const telemetry = useGuestRegistryTelemetry();
  const { eventInfo } = useEventInfo();
  const { value: registryGiftWrapDefaultUSABillingAddress, payload: registryGiftWrapDefaultUSABillingAddressPayload } = useFeatureValue('registryGiftWrapDefaultUSABillingAddress');
  const stripe = useStripe();
  const routes = useRoutePaths();
  const router = useRouterHelper();
  const history = useHistory();
  const [shippingOption, setShippingOption] = useState(coupleAddress ? ExpressCheckoutShippingOption.CoupleAddress : ExpressCheckoutShippingOption.ShipToMe);
  const [giftWrap, setGiftWrap] = useState<GiftWrapOfferState | null>(null);
  const [createRegistryPaymentIntentMutation] = useCreateRegistryPaymentIntentMutation();
  const { t2 } = useTranslation('sharedRegistry');
  const trans = t2('shoppingCart');
  const messageGiverNamePlaceholder = trans.messageStep.giverNamePlaceholder;
  const giftWrapPrice = useGiftWrapECardPrice();
  const eCardPrice = useMemo(() => ({ valueInMinorUnits: giftWrapPrice * 100 }), [giftWrapPrice]);
  const [saveCardDraft] = useSaveCardDraft();
  const setFullPageLoading = useFullPageLoader({ key: 'ExpressCheckout', initialIsLoading: false });
  const [checkoutError, setCheckoutError] = useState<CheckoutError | null>(null);

  const purchaseProducts = useMemo(() => {
    const purchaseItemsMap = new Map(purchaseItems.map(item => [item.registryItemId, item]));
    return allProducts.products.reduce<ProductWithQuantity[]>((acc, product) => {
      const purchaseItem = purchaseItemsMap.get(product.registryItemId);
      if (!purchaseItem) {
        return acc;
      }

      if (product.donationFund) {
        const quantity = purchaseItem.quantity || product.donationFund.remaining * 100 || product.donationFund.goal * 100 || 1_000_00;
        acc.push({ ...product, quantity, totalValueInMinorUnits: quantity });
      } else if (isPricedProduct(product)) {
        const quantity = Math.min(product.stillNeeded, purchaseItem.quantity || product.stillNeeded);
        acc.push({ ...product, quantity, totalValueInMinorUnits: product.valueInMinorUnits * quantity });
      }

      return acc;
    }, []);
  }, [allProducts, purchaseItems]);
  const hasPhysicalProducts = useMemo(() => purchaseProducts.some(product => product.registryItemType === 'product'), [purchaseProducts]);
  // Outside the allowed location, only the free basic message is offered; the paid eCard is disabled rather than hiding Gift Wrap entirely.
  const disablePaidGiftWrap = useMemo<boolean>(() => {
    if (!purchaseProducts.length) {
      return true;
    }
    const { allow } = canShowGW(
      registryState?.shippingAddress,
      eventInfo?.locationInfo,
      registryGiftWrapDefaultUSABillingAddress && isBillingAddressValid(registryGiftWrapDefaultUSABillingAddressPayload)
        ? registryGiftWrapDefaultUSABillingAddressPayload.address
        : undefined,
    );
    return !allow;
  }, [eventInfo?.locationInfo, registryGiftWrapDefaultUSABillingAddress, registryGiftWrapDefaultUSABillingAddressPayload, registryState?.shippingAddress, purchaseProducts.length]);

  const lineItems = useMemo(() => purchaseProducts?.map(product => ({ registryItemId: product.registryItemId, quantity: product.quantity })), [purchaseProducts]);

  const shippingAddress = shippingAddressFragmentAsInput(coupleAddress);
  const getCalculateRegistryPurchaseInfoQuery = useGetCalculateRegistryPurchaseInfoQuery({
    variables: {
      payload: {
        eventId: eventId,
        address: shippingAddress,
        lineItems,
      },
    },
    skip: !lineItems.length,
    batchMode: 'fast',
  });
  const calculateRegistryPurchaseInfo = getCalculateRegistryPurchaseInfoQuery.data?.calculateRegistryPurchaseInfo;
  const costInfo = useMemo(() => computeRegistryPurchaseInfoCostLines(calculateRegistryPurchaseInfo), [calculateRegistryPurchaseInfo]);
  const isFreeShipping = costInfo?.isFreeShipping ?? true;
  const totalAllCostInMinorUnits = costInfo?.totalAllCost.valueInMinorUnits ?? 0;

  const { isLowInventory } = deriveAggregateCheckoutCodesResult([calculateRegistryPurchaseInfo]) ?? {};

  const productsPrice = useMemo<MonetaryValue>(
    () => ({ valueInMinorUnits: purchaseProducts.reduce((acc, product) => acc + product.totalValueInMinorUnits, 0) }),
    [purchaseProducts],
  );
  const currencyCode = useMemo(() => purchaseProducts.at(0)?.currencyCode ?? registryState.registryCurrencyCode ?? DEFAULT_CURRENCY, [purchaseProducts, registryState]);
  const supportsShipToMe = !isLowInventory;

  const totalPrice = useMemo<MonetaryValue>(() => {
    let totalPriceInMinorUnits = productsPrice.valueInMinorUnits + totalAllCostInMinorUnits;

    if (giftWrap?.wrapOption === 'eCard' && eCardPrice) {
      totalPriceInMinorUnits += eCardPrice.valueInMinorUnits;
    }
    return { valueInMinorUnits: totalPriceInMinorUnits };
  }, [productsPrice, giftWrap, eCardPrice, totalAllCostInMinorUnits]);

  useEffect(() => {
    if (!elements || !totalPrice.valueInMinorUnits) {
      return;
    }

    elements.update({
      amount: totalPrice.valueInMinorUnits,
      currency: currencyCode.toLowerCase(),
      mode: 'payment',
    });
  }, [elements, totalPrice, currencyCode, registryState]);

  const createECard = useCallback(
    async ({ message, themeId }: Pick<GiftWrapOfferState, 'message' | 'themeId'>): Promise<ECard | null> => {
      const product = purchaseProducts.at(0);
      if (!product) {
        return null;
      }

      let productLink;
      try {
        const origin = window?.location?.origin ?? 'https://withjoy.com';
        productLink = new URL(`${eventHandle}/edit/registry/track`, origin)?.href;
      } catch (e) {
        // Ignore invalid URL construction; fallback to empty gift link.
      }

      const payload = {
        message,
        themeId,
        image: product.image,
        title: product.title,
        giftLink: productLink ?? '',
      };
      const draftId = await saveCardDraft(eventId, payload);

      if (typeof draftId === 'string') {
        return prepareECardPayload({
          price: eCardPrice.valueInMinorUnits / 100,
          draftId,
          theme: themeId,
          imageURL: product.image,
          offerType: GiftWrapType.CART,
        });
      }
      return null;
    },
    [eCardPrice, purchaseProducts, saveCardDraft, eventHandle, eventId],
  );

  const handleCheckoutConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      // Per Stripe's contract, exactly one of `event.paymentFailed()` or a payment
      // submission (`stripe.confirmPayment`) may happen per confirm event. Once
      // confirmPayment is called, paymentFailed throws "Unexpected call to
      // paymentFailed()". Gate the call so post-submission errors only update UI.
      let paymentSubmitted = false;
      const handleError = (error: CheckoutError) => {
        telemetry.checkoutError(error);
        setCheckoutError(error);
        if (!paymentSubmitted) {
          event.paymentFailed({ reason: 'fail' });
        }
      };
      if (!stripe || !elements || !lineItems.length || !event.billingDetails?.email) {
        return handleError({ code: InternalCheckoutErrorCode.insufficientData });
      }
      if (!giftWrap) {
        return handleError({ code: InternalCheckoutErrorCode.insufficientData });
      }

      try {
        setFullPageLoading(true);

        const giverName = event.shippingAddress?.name ?? event.billingDetails.name;
        const giftMessage = giftWrap ? giftWrap.message.replace(messageGiverNamePlaceholder, giverName) : '';
        let eCard: ECard | null = null;
        if (giftWrap?.wrapOption === 'eCard') {
          eCard = await createECard({ themeId: giftWrap.themeId, message: giftMessage });
        }

        const { error: submitError } = await elements.submit();
        if (submitError) {
          return handleError(submitError);
        }

        const returnUrlRoute = withWindow(global => global.location.origin + routes.guestRegistry.path + '/purchase', '');
        const returnUrlTemplate = composePurchaseReturnUrlTemplate(returnUrlRoute);
        let paymentIntentResult: Awaited<ReturnType<typeof createRegistryPaymentIntentMutation>>;
        try {
          paymentIntentResult = await createRegistryPaymentIntentMutation({
            variables: {
              payload: {
                eventId,
                name: giverName,
                email: event.billingDetails.email,
                note: giftMessage || undefined,
                lineItems,
                returnUrlTemplate,
                eCard,
                address:
                  event.shippingAddress && shippingOption === ExpressCheckoutShippingOption.ShipToMe
                    ? {
                        name: event.shippingAddress.name,
                        address1: event.shippingAddress.address.line1,
                        address2: event.shippingAddress.address.line2,
                        city: event.shippingAddress.address.city,
                        countryCode: findAlpha3ByAlpha2(event.shippingAddress.address.country),
                        country: findCountryNameByAlpha2Code(event.shippingAddress.address.country) ?? event.shippingAddress.address.country,
                        postalCode: event.shippingAddress.address.postal_code,
                        state: event.shippingAddress.address.state,
                        phone: event.billingDetails.phone,
                      }
                    : null,
              },
            },
          });
        } catch (ex) {
          return handleError({ code: InternalCheckoutErrorCode.serverError });
        }

        const paymentIntent = paymentIntentResult.data?.createRegistryPaymentIntent;
        if (paymentIntent?.__typename !== 'RegistryPaymentIntent') {
          const code = (paymentIntent && 'checkoutFailureCode' in paymentIntent && paymentIntent.checkoutFailureCode) || InternalCheckoutErrorCode.serverError;
          return handleError({ code });
        }

        if (!paymentIntent.clientSecret) {
          return handleError({ code: InternalCheckoutErrorCode.missingClientSecret });
        }

        paymentSubmitted = true;
        const { error } = await stripe.confirmPayment({
          elements,
          clientSecret: paymentIntent.clientSecret,
          confirmParams: {
            return_url: paymentIntent.returnUrl,
          },
          redirect: 'if_required',
        });
        if (error) {
          return handleError(error);
        }

        telemetry.checkoutSuccess({
          giftWrapOption: giftWrap?.wrapOption ?? 'giftNote',
          shippingOption,
          lineItems,
          productsPriceInMinorUnits: productsPrice.valueInMinorUnits,
          totalPriceInMinorUnits: totalPrice.valueInMinorUnits,
          expressPaymentType: event.expressPaymentType,
        });
        const returnUrl = new URL(paymentIntent.returnUrl);
        history.replace(returnUrl.pathname + returnUrl.search);
      } catch (ex) {
        handleError({ code: InternalCheckoutErrorCode.unknownError });
      } finally {
        setFullPageLoading(false);
      }
    },
    [
      stripe,
      elements,
      giftWrap,
      shippingOption,
      routes,
      createECard,
      createRegistryPaymentIntentMutation,
      eventId,
      setFullPageLoading,
      history,
      setCheckoutError,
      telemetry,
      totalPrice,
      productsPrice,
      lineItems,
      messageGiverNamePlaceholder,
    ],
  );

  /**
   * compose the Stripe Checkout payload for an Express Checkout
   *   its "standard" Checkout equivalent is in `registry_service` + 'CommerceHelper.createCommerceOrder'
   */
  const handleCheckoutClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      telemetry.expressCheckoutClicked({ ...event, source: 'CheckoutPage' });
      const stripeLineItems: LineItem[] = purchaseProducts.map(product => ({ name: `${product.quantity} x ${product.title}`, amount: product.totalValueInMinorUnits }));
      if (giftWrap?.wrapOption === 'eCard') {
        stripeLineItems.push({ name: 'Personalized eCard', amount: eCardPrice.valueInMinorUnits });
      }
      costInfo?.orderedAllCostLines.forEach(line => {
        stripeLineItems.push({ name: line.title, amount: line.lineValue?.valueInMinorUnits ?? 0 });
      });
      event.resolve({
        billingAddressRequired: true,
        emailRequired: true,
        lineItems: stripeLineItems,
        ...(shippingOption === ExpressCheckoutShippingOption.ShipToMe
          ? {
              shippingAddressRequired: true,
              phoneNumberRequired: true,
              shippingRates: [
                {
                  id: 'default',
                  displayName: isFreeShipping ? trans.shippingAddressStep.freeShipping : trans.shippingAddressStep.standardShipping,
                  amount: costInfo?.deliveryTotalValue.valueInMinorUnits ?? 0,
                },
              ],
            }
          : {}),
      });
    },
    [shippingOption, purchaseProducts, trans, giftWrap, eCardPrice, telemetry, costInfo, isFreeShipping],
  );

  const handleCheckoutReady = useCallback(
    (event: StripeExpressCheckoutElementReadyEvent) => {
      if (event.availablePaymentMethods && isSomePaymentMethodAvailable(event.availablePaymentMethods)) {
        return;
      }
      // redirect back to registry if no available payment methods
      history.replace(router.buildPath(''));
    },
    [history, router],
  );

  useEffect(() => {
    if (purchaseProducts.length) {
      return;
    }
    // redirect back to registry if no items to purchase
    history.replace(router.buildPath(''));
  }, [purchaseProducts, history, router]);

  return (
    <Provider
      value={{
        state: {
          purchaseProducts,
          hasPhysicalProducts,
          disablePaidGiftWrap,
          currencyCode,
          giftWrap,
          costInfo,
          shippingOption,
          coupleAddress,
          supportsShipToMe,
          productsPrice,
          totalPrice,
          eCardPrice,
          checkoutError,
          messageGiverNamePlaceholder,
        },
        mutators: {
          setShippingOption,
          setGiftWrap,
          handleCheckoutClick,
          handleCheckoutConfirm,
          handleCheckoutReady,
        },
      }}
    >
      {children}
    </Provider>
  );
};
